import { useRef, useState, type HTMLAttributes } from 'react';

/** Reorders only siblings; never changes ownership or folder membership. */
export function reorderedIds(
  ids: readonly string[],
  source: string,
  target: string,
  after: boolean,
): string[] {
  if (source === target || !ids.includes(source) || !ids.includes(target)) return [...ids];
  const next = ids.filter((id) => id !== source);
  next.splice(next.indexOf(target) + (after ? 1 : 0), 0, source);
  return next;
}

export function useSiblingReorder(save?: (ids: string[]) => Promise<unknown>) {
  const source = useRef<string | null>(null);
  const busy = useRef(false);
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState('');
  const [drop, setDrop] = useState<{ id: string; after: boolean } | null>(null);
  async function commit(ids: readonly string[], from: string, to: string, after: boolean) {
    if (!save || busy.current) return;
    const next = reorderedIds(ids, from, to, after);
    if (next.every((id, i) => id === ids[i])) return;
    busy.current = true;
    setPending(true);
    setMessage('Saving order…');
    try {
      await save(next);
      setMessage('Order saved.');
    } catch {
      setMessage('Could not save order. Your previous order is unchanged. Please try again.');
    } finally {
      busy.current = false;
      setPending(false);
    }
  }
  function row(id: string, siblings: readonly string[]): HTMLAttributes<HTMLElement> {
    if (!save || siblings.length < 2) return {};
    return {
      draggable: !pending,
      'aria-description':
        'Drag to reorder within this section, or use Alt plus Up or Down arrow. Order is saved.',
      className: `navigation-reorder-row${drop?.id === id ? (drop.after ? ' drop-after' : ' drop-before') : ''}`,
      onDragStart: (e) => {
        e.stopPropagation();
        source.current = id;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', id);
      },
      onDragOver: (e) => {
        if (
          !source.current ||
          source.current === id ||
          !siblings.includes(source.current) ||
          busy.current
        )
          return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        const rect = e.currentTarget.getBoundingClientRect();
        setDrop({ id, after: e.clientY > rect.top + rect.height / 2 });
      },
      onDragLeave: (e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDrop(null);
      },
      onDrop: (e) => {
        e.preventDefault();
        e.stopPropagation();
        const from = source.current;
        const rect = e.currentTarget.getBoundingClientRect();
        const after = e.clientY > rect.top + rect.height / 2;
        source.current = null;
        setDrop(null);
        if (from) void commit(siblings, from, id, after);
      },
      onDragEnd: () => {
        source.current = null;
        setDrop(null);
      },
      onKeyDown: (e) => {
        if (!e.altKey || !['ArrowUp', 'ArrowDown'].includes(e.key)) return;
        e.preventDefault();
        e.stopPropagation();
        const down = e.key === 'ArrowDown';
        const target = siblings[siblings.indexOf(id) + (down ? 1 : -1)];
        if (target) void commit(siblings, id, target, down);
      },
    };
  }
  return { row, message, pending };
}
